import { usePreventRemove } from '@react-navigation/native'
import { IconPhotoStar, IconPhotoX, IconStar } from '@tabler/icons-react-native'
import { Image } from 'expo-image'
import { useFocusEffect, useNavigation, useSegments } from 'expo-router'
import { includes, sample } from 'lodash'
import { ComponentRef, useCallback, useRef, useState, type FC } from 'react'
import { Zoom } from 'react-native-reanimated-zoom'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type PhotoView_photo$key } from '@/__generated__/PhotoView_photo.graphql'
import { type PhotoView_query$key } from '@/__generated__/PhotoView_query.graphql'
import { type PhotoViewDeletePhotoMutation } from '@/__generated__/PhotoViewDeletePhotoMutation.graphql'
import { type PhotoViewMakePrimaryMutation } from '@/__generated__/PhotoViewMakePrimaryMutation.graphql'
import { type PhotoViewUpdatePhotoMutation } from '@/__generated__/PhotoViewUpdatePhotoMutation.graphql'
import placeholder from '@/assets/images/placeholder.png'
import { ContainedCard, Dialog, HeaderButton, HStack, Icon, Screen, Spinner, Text, VStack } from '@/components'
import { TextArea, TextAreaInput } from '@/components/gluestack/textarea'
import { useDisclosure, usePreloadableRouter } from '@/hooks'
import {
  COLOR_BRAND_YELLOW_500,
  COLOR_WHITE,
  getPublicImageUrl,
  PHOTO_DESCRIPTION_MAX,
  PROFILE_PHOTOS_MIN
} from '@/lib'

const placeholderCaptions = [
  'ex: A white brunette woman smiling in front of a cherry blossom in Central Park with her Golden Retriever.',
  'ex: A black nonbinary person playing a red acoustic guitar.',
  'ex: A white male wearing dark rimmed glasses reading a novel.',
  'ex: A group of women posing in front of the Golden Gate Bridge.',
  'ex: A Southeast Asian woman in the middle of her two parents at her college graduation',
  'ex: A black male wheelchair user crossing a marathon finish line.'
]

export interface PhotoViewProps {
  photoRef: PhotoView_photo$key | null | undefined
  queryRef: PhotoView_query$key | null | undefined
}

export const PhotoView: FC<PhotoViewProps> = ({ photoRef, queryRef }) => {
  const deleteButtonRef = useRef<ComponentRef<typeof HeaderButton>>(null)
  const navigation = useNavigation()
  const router = usePreloadableRouter()
  const segments = useSegments()
  const photoData = useFragment(
    graphql`
      fragment PhotoView_photo on Photo {
        description
        rowId
        storageObject {
          blurhash
          bucket
          key
        }
        userId
        user {
          profile {
            headshot {
              rowId
            }
            photos {
              totalCount
            }
          }
        }
      }
    `,
    photoRef
  )
  const queryData = useFragment(
    graphql`
      fragment PhotoView_query on Query {
        currentUser {
          rowId
        }
      }
    `,
    queryRef
  )
  const [deletePhoto, isDeletingPhoto] = useMutation<PhotoViewDeletePhotoMutation>(graphql`
    mutation PhotoViewDeletePhotoMutation($input: DeletePhotoInput!) {
      deletePhoto(input: $input) {
        query {
          currentUser {
            profile {
              photos {
                totalCount
              }
              ...ProfileAvatar_profile
              ...ProfilePhotosView_profile
              ...useVerificationPhotos_profile
            }
          }
        }
      }
    }
  `)
  const [updatePhoto, isUpdatingPhoto] = useMutation<PhotoViewUpdatePhotoMutation>(graphql`
    mutation PhotoViewUpdatePhotoMutation($input: UpdatePhotoInput!) {
      updatePhoto(input: $input) {
        photo {
          description
        }
      }
    }
  `)
  const [makePrimaryPhoto, isMakingPrimaryPhoto] = useMutation<PhotoViewMakePrimaryMutation>(graphql`
    mutation PhotoViewMakePrimaryMutation($input: UpdatePhotoInput!) {
      updatePhoto(input: $input) {
        photo {
          ...PhotoView_photo
        }
        query {
          currentUser {
            profile {
              ...ProfileAvatar_profile
              ...ProfilePhotosView_profile
            }
          }
        }
      }
    }
  `)
  const [caption, setCaption] = useState(photoData?.description)
  const [showDeleteDialog, deleteDialog] = useDisclosure(false)
  const blurhash = photoData?.storageObject?.blurhash
  const isAuthor = queryData?.currentUser?.rowId === photoData?.userId
  const isDisabled = isDeletingPhoto || isUpdatingPhoto || isMakingPrimaryPhoto
  const isPrimary =
    !!photoData?.user?.profile?.headshot?.rowId && photoData.rowId === photoData.user.profile.headshot.rowId
  const photoCount = photoData?.user?.profile?.photos?.totalCount ?? 0
  const hasMinimumPhotos = photoCount === PROFILE_PHOTOS_MIN

  usePreventRemove(caption !== photoData?.description, ({ data }) =>
    updatePhoto({
      onCompleted: () => navigation.dispatch(data.action),
      variables: {
        input: {
          rowId: photoData?.rowId,
          patch: {
            description: caption
          }
        }
      }
    })
  )

  useFocusEffect(
    useCallback(() => {
      navigation.setOptions({
        headerRight: () => {
          if (isAuthor) {
            return (
              <HStack className='p-2'>
                {isUpdatingPhoto ? (
                  <Spinner
                    color={COLOR_WHITE}
                    size='small'
                  />
                ) : null}
                {!isPrimary ? (
                  <HeaderButton
                    accessibilityLabel='Make primary photo'
                    accessibilityState={{ busy: isMakingPrimaryPhoto, disabled: isDisabled }}
                    disabled={isDisabled}
                    IconComponent={IconPhotoStar}
                    testID='PhotoScreen.makePrimaryButton'
                    onPress={() =>
                      makePrimaryPhoto({
                        variables: {
                          input: {
                            rowId: photoData?.rowId,
                            patch: {
                              sequence: 1
                            }
                          }
                        }
                      })
                    }
                  />
                ) : null}
                {!hasMinimumPhotos ? (
                  <HeaderButton
                    accessibilityLabel='Delete photo'
                    disabled={isDisabled}
                    IconComponent={IconPhotoX}
                    onPress={deleteDialog.open}
                    ref={deleteButtonRef}
                    testID='PhotoScreen.deleteButton'
                  />
                ) : null}
              </HStack>
            )
          }

          return null
        }
      })
    }, [
      deleteDialog.open,
      hasMinimumPhotos,
      isAuthor,
      isDisabled,
      isMakingPrimaryPhoto,
      isPrimary,
      isUpdatingPhoto,
      makePrimaryPhoto,
      navigation,
      photoData?.rowId
    ])
  )

  return (
    <Screen withCenterHorizontal>
      <ContainedCard>
        <HStack className='relative overflow-hidden rounded-lg border border-brand-grey-100'>
          <Zoom
            maximumZoomScale={3}
            className='aspect-square w-full'
          >
            <Image
              accessible
              accessibilityLabel={photoData?.description || 'Profile Photo'}
              cachePolicy='memory-disk'
              className='aspect-square w-full'
              contentFit='cover'
              contentPosition='center'
              placeholder={blurhash ? { blurhash } : placeholder}
              placeholderContentFit='cover'
              source={{
                uri: getPublicImageUrl(photoData?.storageObject?.bucket, photoData?.storageObject?.key)
              }}
            />
          </Zoom>
          {isPrimary && isAuthor ? (
            <HStack
              accessible
              accessibilityLabel='Primary photo'
              className='absolute left-2 top-2'
            >
              <Icon
                as={IconStar}
                className='h-7 w-7'
                fill={COLOR_BRAND_YELLOW_500}
                stroke={COLOR_WHITE}
                strokeWidth={1}
              />
            </HStack>
          ) : null}
        </HStack>
        {isAuthor ? (
          <TextArea
            className='rounded-lg border-brand-grey-100 data-[focus=true]:border-brand-grey-100 data-[focus=true]:data-[hover=true]:border-brand-grey-100 data-[hover=true]:border-brand-grey-100 data-[disabled=true]:data-[hover=true]:border-brand-grey-100'
            isDisabled={isDisabled}
          >
            <TextAreaInput
              accessibilityHint='Tap to enter a caption for this photo'
              accessibilityLabel='Photo Caption text input'
              autoCorrect
              className='text-black'
              maxLength={PHOTO_DESCRIPTION_MAX}
              onChangeText={(value) => setCaption(value || null)}
              placeholder={sample(placeholderCaptions)}
              testID='PhotoScreen.captionInput'
              value={caption || ''}
            />
          </TextArea>
        ) : (
          <Text className='p-1 font-poppinsMedium text-black'>{photoData?.description ?? 'No caption'}</Text>
        )}
      </ContainedCard>
      {isAuthor ? (
        <Text className='text-sm text-white'>Tip: Image captions are helpful to people using screen readers!</Text>
      ) : null}
      {isAuthor && hasMinimumPhotos ? (
        <VStack className='flex-1 justify-end gap-1'>
          <Text className='text-center text-sm text-white'>You must have at least {PROFILE_PHOTOS_MIN} photos.</Text>
          <Text className='text-center text-sm text-white'>Add another photo before deleting this one.</Text>
        </VStack>
      ) : null}
      <Dialog
        finalFocusRef={deleteButtonRef}
        isOpen={showDeleteDialog}
        onClose={deleteDialog.close}
        onConfirm={useCallback(() => {
          deletePhoto({
            onCompleted: () => {
              deleteDialog.close()

              if (includes(segments, 'onboarding')) {
                router.dismissTo('/onboarding/photos', {
                  queryName: 'OnboardingPhotosScreenQuery'
                })
              } else {
                router.dismissTo('/profile/photos', {
                  queryName: 'ProfilePhotosScreenQuery'
                })
              }
            },
            variables: {
              input: {
                rowId: photoData?.rowId
              }
            }
          })
        }, [deleteDialog, deletePhoto, photoData?.rowId, router, segments])}
        title='Delete Photo'
      >
        Are you sure you want to delete this photo? This cannot be undone!
      </Dialog>
    </Screen>
  )
}
